import { Controller } from "@hotwired/stimulus"

// Mention autocomplete controller
// Detects @ in chat input and shows participant suggestions
export default class extends Controller {
  static targets = ["input", "dropdown", "list"]
  static values = { roomId: Number }

  connect() {
    this.participants = []
    this.selectedIndex = -1
    this.isOpen = false

    this.loadParticipants()
  }

  async loadParticipants() {
    try {
      const response = await fetch(`/chat/rooms/${this.roomIdValue}/participants.json`)
      if (response.ok) {
        const data = await response.json()
        this.participants = data.map(p => ({
          mb_id: p.mb_id,
          nickname: p.nickname || p.member?.mb_nick || p.mb_id,
          profile_color: p.profile_color || "#DC2626",
          role: p.role
        }))
      }
    } catch (error) {
      console.error("Failed to load participants:", error)
    }
  }

  handleInput(event) {
    const input = event.target
    const cursorPosition = input.selectionStart
    const text = input.value.substring(0, cursorPosition)

    // Check if we're in a mention context (@ followed by text, not inside [[]])
    const mentionMatch = text.match(/@([\w가-힣]*)$/)
    const insideBrackets = text.match(/\[\[[^\]]*$/)

    if (mentionMatch && !insideBrackets) {
      const query = mentionMatch[1].toLowerCase()
      this.showSuggestions(query)
    } else {
      this.hideSuggestions()
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        event.stopPropagation()
        this.moveSelection(1)
        break
      case "ArrowUp":
        event.preventDefault()
        event.stopPropagation()
        this.moveSelection(-1)
        break
      case "Enter":
      case "Tab":
        if (this.selectedIndex >= 0) {
          event.preventDefault()
          event.stopPropagation()
          this.selectCurrentItem()
        }
        break
      case "Escape":
        event.preventDefault()
        this.hideSuggestions()
        break
    }
  }

  showSuggestions(query) {
    const filtered = this.participants.filter(p =>
      p.nickname.toLowerCase().includes(query) ||
      p.mb_id.toLowerCase().includes(query)
    ).slice(0, 8)

    if (filtered.length === 0) {
      this.hideSuggestions()
      return
    }

    this.renderSuggestions(filtered)
    this.isOpen = true
    this.selectedIndex = 0
    this.updateSelection()
    this.dropdownTarget.classList.remove("hidden")
  }

  hideSuggestions() {
    this.isOpen = false
    this.selectedIndex = -1
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  renderSuggestions(items) {
    this.listTarget.innerHTML = items.map((item, index) => `
      <li class="px-3 py-2 cursor-pointer hover:bg-red-50 flex items-center ${index === this.selectedIndex ? 'bg-red-100' : ''}"
          data-action="click->mention-autocomplete#selectItem"
          data-mb-id="${this.escapeHtml(item.mb_id)}"
          data-nickname="${this.escapeHtml(item.nickname)}">
        <div class="h-8 w-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0"
             style="background-color: ${item.profile_color}">
          <span class="text-white text-sm font-medium">${this.escapeHtml(item.nickname.charAt(0))}</span>
        </div>
        <div class="flex-1 min-w-0">
          <span class="font-medium text-gray-900">${this.escapeHtml(item.nickname)}</span>
          <span class="text-gray-500 text-sm ml-1">@${this.escapeHtml(item.mb_id)}</span>
        </div>
        ${item.role === 'owner' ? '<span class="text-xs text-yellow-600 ml-2">방장</span>' : ''}
        ${item.role === 'admin' ? '<span class="text-xs text-red-600 ml-2">관리자</span>' : ''}
      </li>
    `).join("")
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  moveSelection(direction) {
    const items = this.listTarget.querySelectorAll("li")
    this.selectedIndex = Math.max(0, Math.min(items.length - 1, this.selectedIndex + direction))
    this.updateSelection()
  }

  updateSelection() {
    const items = this.listTarget.querySelectorAll("li")
    items.forEach((item, index) => {
      item.classList.toggle("bg-red-100", index === this.selectedIndex)
    })
  }

  selectCurrentItem() {
    const items = this.listTarget.querySelectorAll("li")
    const selected = items[this.selectedIndex]
    if (selected) {
      this.insertMention(selected.dataset.nickname)
    }
  }

  selectItem(event) {
    const nickname = event.currentTarget.dataset.nickname
    this.insertMention(nickname)
  }

  insertMention(nickname) {
    const input = this.inputTarget
    const cursorPosition = input.selectionStart
    const text = input.value

    // Find the @ symbol position
    const beforeCursor = text.substring(0, cursorPosition)
    const atPosition = beforeCursor.lastIndexOf("@")

    if (atPosition >= 0) {
      const before = text.substring(0, atPosition)
      const after = text.substring(cursorPosition)
      input.value = `${before}@${nickname} ${after}`
      const newPosition = atPosition + nickname.length + 2
      input.selectionStart = input.selectionEnd = newPosition
    }

    this.hideSuggestions()
    input.focus()

    // Trigger input event for other controllers
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }
}
